
import type { BrowseConfigurationsConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/browse-configurations-config'
import type { ConfigurationAttributeConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/configuration-attribute-config'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { ApiError, Pivot } from '@empathyco/playboard-core-domain'
import type { ConfigurationsMessages, ContextState, RegularFilter, Tab } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import FieldValues from '../../components/field-values.component.vue'
import RegularFilterSelector from '../../components/filters/regular-filter-selector.component.vue'
import { EpbText } from '../../components/shared/epb-text'
import EpbTabs from '../../components/tabs.component.vue'
import { useApiError } from '../../composables/use-api-error'
import { useAppLoading } from '../../composables/use-app-loading'
import { useFieldValues } from '../../composables/use-field-values'
import { useToolingPivots } from '../../composables/use-tooling-pivots'
import { mapActions, mapGetters } from '../../composables/utils'
import { FeatureIdEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { ArrayUtils } from '../../utils/array.utils'

export default defineComponent({
  name: 'BrowseConfigurationsRoot',
  components: {
    EpbTabs,
    EpbText,
    FieldValues,
    RegularFilterSelector,
  },
  async beforeRouteLeave(to, from, next) {
    this.contextWatchDisabled = true
    next()
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const { instance } = mapGetters<Instance>(ctx.proxy.$store, CONFIG_CONSTS.moduleName, {
      instance: CONFIG_CONSTS.getters.currentInstance,
    })

    const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const contextActions = mapActions(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      setFiltersSelectedValues: CONTEXT_CONSTS.actions.setFiltersSelectedValues,
      setQuery: CONTEXT_CONSTS.actions.setQuery,
    })

    const { loading } = useAppLoading()

    const pivotable = ref<Array<Pivot>>([])
    const { visiblePivots, ...toolingPivotsComposable } = useToolingPivots({
      pivotable,
      extraConfig: {
        default: {
          mandatory: true,
          multiselect: false,
        },
      },
    })

    const { fieldValues: attributeValues, ...fieldValuesComposable } = useFieldValues({
      reset: true,
    })

    const { getErrorMessage } = useApiError()

    const baseClass = 'epb-browse-configurations-root'

    const messages = ctx.proxy.$epb.messages.tools.configurations as ConfigurationsMessages

    const routeNames = ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE]

    const attributeName = ref<string | undefined>(undefined)

    const attributeValue = ref<string | undefined>(undefined)

    const toolConfig = computed<BrowseConfigurationsConfig>(() => {
      return (
        instance.value.domains[DomainEnum.BROWSE]?.tools[FeatureIdEnum.CONFIGURATION] ?? {
          attributes: [],
          useSearchConfigurations: false,
        }
      )
    })

    const attributeNameTabs = computed<Array<Tab>>(() => {
      return toolConfig.value.attributes.map(attributeConfig => ({
        id: attributeConfig.id,
        text: capitalizeSentence(getAttributeLabel(attributeConfig)),
      }))
    })

    const attributeNameLabel = computed<string | undefined>(() => {
      const attributeConfig = toolConfig.value.attributes.find(
        attributeConfig => attributeConfig.id === attributeName.value,
      )
      return attributeConfig ? getAttributeLabel(attributeConfig) : undefined
    })

    const query = computed<string>(() => context.value.query ?? '')

    function initPivotsSelectedValues(): void {
      let contextPivotsChanged = false
      toolingPivotsComposable.pivots.value.forEach(pivot => {
        let selectedValues: RegularFilter['selectedValues']
        const contextFilter = context.value.filters[pivot.config.id] as RegularFilter
        if (contextFilter?.selectedValues.length) {
          selectedValues = contextFilter.selectedValues
        } else {
          selectedValues = toolingPivotsComposable.getPivotDefaultValues(pivot, true)
          contextPivotsChanged = true
        }
        toolingPivotsComposable.setPivotSelectedValues(pivot, selectedValues)
      })
      if (contextPivotsChanged) {
        updateContextPivots()
      }
    }

    async function updateContextPivots(): Promise<void> {
      await contextActions.setFiltersSelectedValues(
        toolingPivotsComposable.pivots.value.reduce<
          Record<string, RegularFilter['selectedValues']>
        >(
          (result, pivot) => ({
            ...result,
            [pivot.config.id]: pivot.selectedValues,
          }),
          {},
        ),
      )
      emit('contextChanged')
    }

    async function setPivotSelectedValues(
      pivot: RegularFilter,
      selectedValues: RegularFilter['selectedValues'],
    ): Promise<void> {
      toolingPivotsComposable.setPivotSelectedValues(pivot, selectedValues)
      await updateContextPivots()
      await loadAttributeValues()
    }

    function initAttributeName(): void {
      const routeAttributeName = ctx.proxy.$route.params[ROUTER_CONSTS.params.attribute.name]
      if (
        routeAttributeName &&
        toolConfig.value.attributes.some(attribute => attribute.id === routeAttributeName)
      ) {
        attributeName.value = routeAttributeName
      } else {
        attributeName.value = toolConfig.value.attributes[0]?.id
      }
    }

    async function setAttributeName(value: string): Promise<void> {
      attributeName.value = value
      attributeValue.value = undefined
      ctx.proxy.$router.replace({
        ...ctx.proxy.$route,
        name: routeNames.attributes,
        params: {
          ...ctx.proxy.$route.params,
          [ROUTER_CONSTS.params.attribute.name]: value,
          [ROUTER_CONSTS.params.attribute.value]: undefined,
        },
      })
      await setQuery('')
      await loadAttributeValues()
    }

    function getAttributeLabel(attributeConfig: ConfigurationAttributeConfig): string {
      return (
        attributeConfig.prettyName || messages.attributes[attributeConfig.id] || attributeConfig.id
      )
    }

    async function setQuery(value: string, disableRouteUpdate = false): Promise<void> {
      await contextActions.setQuery(value || undefined)
      if (!disableRouteUpdate) {
        emit('contextChanged')
      }
    }

    async function loadAttributeValues(callback?: () => void): Promise<void> {
      try {
        emit('updateLoading', { visible: true })
        await fieldValuesComposable.loadFieldValues({
          pivots: pivotable.value,
          attribute: attributeName.value,
        })
      } catch (error) {
        emit('showError', getErrorMessage(error as ApiError))
      } finally {
        callback?.()
        emit('updateLoading', { visible: false })
      }
    }

    function initAttributeValue(): void {
      const routeAttributeName = ctx.proxy.$route.params[ROUTER_CONSTS.params.attribute.name]
      const routeAttributeValue = ctx.proxy.$route.params[ROUTER_CONSTS.params.attribute.value]
      if (
        routeAttributeValue &&
        ArrayUtils.findInHierarchy(
          routeAttributeValue,
          attributeValues.value[routeAttributeName] ?? [],
        ).length
      ) {
        attributeValue.value = routeAttributeValue
      }
    }

    function setAttributeValue(value: string): void {
      attributeValue.value = value
      ctx.proxy.$router.push({
        ...ctx.proxy.$route,
        name: routeNames.list,
        params: {
          ...ctx.proxy.$route.params,
          [ROUTER_CONSTS.params.attribute.value]: value,
        },
      })
    }

    function initRoute(): void {
      let routeName: string
      if (attributeName.value) {
        if (attributeValue.value) {
          routeName = ctx.proxy.$route.name
        } else {
          routeName = routeNames.attributes
        }
      } else {
        routeName = ROUTER_CONSTS.routes.pageNotFound
      }
      ctx.proxy.$router.replace({
        ...ctx.proxy.$route,
        name: routeName,
        params: {
          ...ctx.proxy.$route.params,
          [ROUTER_CONSTS.params.attribute.name]: attributeName.value,
          [ROUTER_CONSTS.params.attribute.value]: attributeValue.value,
        },
      })
    }

    initPivotsSelectedValues()
    initAttributeName()
    loadAttributeValues(() => {
      initAttributeValue()
      initRoute()
      watch(
        () => ctx.proxy.$route,
        value => {
          const routeAttributeName = value.params[ROUTER_CONSTS.params.attribute.name]
          const routeAttributeValue =
            value.name !== routeNames.attributes
              ? value.params[ROUTER_CONSTS.params.attribute.value]
              : undefined
          if (
            routeAttributeName !== attributeName.value ||
            routeAttributeValue !== attributeValue.value
          ) {
            attributeName.value = routeAttributeName
            attributeValue.value = undefined
            loadAttributeValues()
          }
        },
      )
    })

    return {
      baseClass,
      messages,
      attributeName,
      attributeValues,
      attributeValue,
      visiblePivots,
      attributeNameTabs,
      attributeNameLabel,
      query,
      loading,
      setPivotSelectedValues,
      setAttributeName,
      setQuery,
      setAttributeValue,
    }
  },
})
